import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { HeronMark, IconCheck, IconShield } from '../components/icons';
import { Button, Callout, ErrorNote, TextField } from '../components/ui';

/**
 * The only page a customer ever sees. Reached from the invite email.
 *
 * The token in the URL IS the authentication, so it is validated server-side
 * before the form appears — showing a password form behind a dead link only to
 * reject it on submit wastes the one attempt a confused user will make.
 */

interface InvitePreview {
  username: string;
  companyName: string;
  purpose: 'invite' | 'reset';
  emailHint: string | null;
}

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [linkError, setLinkError] = useState<unknown>(null);
  const [checking, setChecking] = useState(true);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkError(
        new ApiError(400, 'TOKEN_INVALID', 'This link is missing its token. Use the link from your email exactly as it was sent.'),
      );
      setChecking(false);
      return;
    }

    let cancelled = false;
    api
      .get<InvitePreview>(`/api/public/invite/${encodeURIComponent(token)}`)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLinkError(error);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const mismatch = confirm.length > 0 && password !== confirm;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mismatch) return;
    setSubmitError(null);
    setBusy(true);
    try {
      await api.post('/api/public/set-password', { token, password });
      setDone(true);
    } catch (error) {
      setSubmitError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <div className="gate__card">
        <div className="gate__brand">
          <HeronMark size={24} />
          <span>Heron License</span>
        </div>

        {checking && <p className="muted">Checking your link…</p>}

        {!checking && linkError !== null && (
          <>
            <h1 className="gate__title">This link can’t be used</h1>
            <div className="stack">
              <ErrorNote error={linkError} />
              <p className="small muted">
                Set-password links are single-use and expire. Ask whoever set up your account to send
                a new one.
              </p>
            </div>
          </>
        )}

        {!checking && preview && done && (
          <>
            <h1 className="gate__title">
              <IconCheck size={20} /> Password set
            </h1>
            <p className="gate__sub">
              You can now sign in to {preview.companyName}’s products with the username{' '}
              <strong className="mono">{preview.username}</strong>.
            </p>
            <Callout tone="info">
              Close this page and return to the product you were given a link for. Heron License
              itself is an internal tool — there is nothing here for you to sign in to.
            </Callout>
          </>
        )}

        {!checking && preview && !done && (
          <form onSubmit={submit}>
            <h1 className="gate__title">
              {preview.purpose === 'invite' ? 'Choose your password' : 'Reset your password'}
            </h1>
            <p className="gate__sub">
              For <strong>{preview.username}</strong> at {preview.companyName}
              {preview.emailHint ? ` (${preview.emailHint})` : ''}.
            </p>

            <div className="gate__fields">
              {submitError ? <ErrorNote error={submitError} /> : null}

              <TextField
                label="New password"
                type="password"
                value={password}
                required
                autoComplete="new-password"
                autoFocus
                hint="At least 10 characters. A short phrase you can remember beats a scrambled word you cannot."
                onChange={(event) => setPassword(event.currentTarget.value)}
              />
              <TextField
                label="Confirm password"
                type="password"
                value={confirm}
                required
                autoComplete="new-password"
                error={mismatch ? 'The two passwords do not match.' : undefined}
                onChange={(event) => setConfirm(event.currentTarget.value)}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                loading={busy}
                disabled={password.length === 0 || mismatch}
              >
                Set password
              </Button>
            </div>

            <p className="gate__foot">
              <IconShield size={14} /> Nobody at our end can see this password — not even support.
              Choose something only you know.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
